// Static data structure for cars
// This represents how the database should be structured in the future

export const carsData = [
  {
    id: '1',
    name: 'Toyota Camry',
    brand: 'Toyota',
    model: 'Camry 2024',
    image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=800',
    price12Hour: 1500, // Price per 12-hour slot
    price24Hour: 2800, // Price per 24-hour slot (discounted from 2x12hr)
    fuelType: 'Petrol',
    transmission: 'Automatic',
    seats: 5,
    mileage: '15 kmpl',
    features: ['AC', 'Music System', 'Bluetooth', 'GPS Navigation', 'Reverse Camera'],
    description: 'Comfortable and reliable sedan perfect for city drives and long journeys.',
    available: true,
    rating: 4.5,
    totalReviews: 120
  },
  {
    id: '2',
    name: 'Honda City',
    brand: 'Honda',
    model: 'City VX',
    image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=800',
    price12Hour: 1300,
    price24Hour: 2400,
    fuelType: 'Petrol',
    transmission: 'Automatic',
    seats: 5,
    mileage: '17 kmpl',
    features: ['AC', 'Music System', 'Bluetooth', 'Sunroof', 'Push Start'],
    description: 'Premium sedan with advanced features and excellent fuel efficiency.',
    available: true,
    rating: 4.3,
    totalReviews: 95
  },
  {
    id: '3',
    name: 'Hyundai Creta',
    brand: 'Hyundai',
    model: 'Creta SX',
    image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=800',
    price12Hour: 1700,
    price24Hour: 3200,
    fuelType: 'Diesel',
    transmission: 'Automatic',
    seats: 5,
    mileage: '18 kmpl',
    features: ['AC', 'Music System', 'Bluetooth', 'GPS', 'Sunroof', 'Reverse Camera'],
    description: 'Spacious SUV ideal for family trips and off-road adventures.',
    available: true,
    rating: 4.6,
    totalReviews: 150
  },
  {
    id: '4',
    name: 'Maruti Swift',
    brand: 'Maruti',
    model: 'Swift ZXI',
    image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=800',
    price12Hour: 1100,
    price24Hour: 2000,
    fuelType: 'Petrol',
    transmission: 'Manual',
    seats: 5,
    mileage: '23 kmpl',
    features: ['AC', 'Music System', 'Bluetooth', 'Power Steering'],
    description: 'Compact and fuel-efficient hatchback perfect for city commuting.',
    available: true,
    rating: 4.2,
    totalReviews: 200
  },
  {
    id: '5',
    name: 'Mahindra XUV700',
    brand: 'Mahindra',
    model: 'XUV700 AX7',
    image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=800',
    price12Hour: 2200,
    price24Hour: 4000,
    fuelType: 'Diesel',
    transmission: 'Automatic',
    seats: 7,
    mileage: '14 kmpl',
    features: ['AC', 'Music System', 'Bluetooth', 'GPS', 'Sunroof', 'Reverse Camera', '360 Camera'],
    description: 'Premium 7-seater SUV with luxury features and powerful performance.',
    available: true,
    rating: 4.7,
    totalReviews: 85
  },
  {
    id: '6',
    name: 'Tata Nexon',
    brand: 'Tata',
    model: 'Nexon XZ+',
    image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=800',
    price12Hour: 1400,
    price24Hour: 2600,
    fuelType: 'Electric',
    transmission: 'Automatic',
    seats: 5,
    mileage: '312 km range',
    features: ['AC', 'Music System', 'Bluetooth', 'GPS', 'Fast Charging', 'Regenerative Braking'],
    description: 'Eco-friendly electric SUV with modern technology and zero emissions.',
    available: true,
    rating: 4.4,
    totalReviews: 110
  }
];

// Bookings data is now fetched from Firebase in real-time
// No static data or localStorage is used

// ==================== BOOKING CALCULATION FUNCTIONS ====================

/**
 * Calculate booking duration in hours
 * @param {string} fromDateTime - ISO format datetime string
 * @param {string} toDateTime - ISO format datetime string
 * @returns {number} - Total hours
 */
export const calculateDurationHours = (fromDateTime, toDateTime) => {
  const from = new Date(fromDateTime);
  const to = new Date(toDateTime);
  const diffMs = to - from;
  const diffHours = diffMs / (1000 * 60 * 60);
  return Math.max(0, diffHours);
};

/**
 * Calculate booking cost with breakdown
 * @param {number} totalHours - Total booking duration in hours
 * @param {number} price12Hour - 12-hour slot price
 * @param {number} price24Hour - 24-hour slot price
 * @returns {object} - Cost breakdown
 */
export const calculateBookingCost = (totalHours, price12Hour, price24Hour) => {
  // Minimum 6 hours
  if (totalHours < 6) {
    return {
      totalHours: 0,
      slots24Hour: 0,
      slots12Hour: 0,
      remainingHours: 0,
      subtotal: 0,
      breakdown: 'Minimum 6 hours required'
    };
  }

  let remainingHours = totalHours;
  let slots24Hour = 0;
  let slots12Hour = 0;

  // Calculate full 24-hour slots
  slots24Hour = Math.floor(remainingHours / 24);
  remainingHours = remainingHours % 24;

  // Calculate remaining hours pricing
  // 1-12 hours = 1 x 12-hour slot
  // 13-24 hours = 1 x 24-hour slot
  if (remainingHours > 0) {
    if (remainingHours <= 12) {
      slots12Hour = 1;
    } else {
      // More than 12 hours remaining, charge as 24-hour slot
      slots24Hour += 1;
      remainingHours = 0;
    }
  }

  const cost24Hour = slots24Hour * price24Hour;
  const cost12Hour = slots12Hour * price12Hour;
  const subtotal = cost24Hour + cost12Hour;

  return {
    totalHours,
    slots24Hour,
    slots12Hour,
    remainingHours,
    cost24Hour,
    cost12Hour,
    subtotal,
    breakdown: generateBreakdownText(slots24Hour, slots12Hour, price24Hour, price12Hour)
  };
};

/**
 * Generate human-readable breakdown text
 */
const generateBreakdownText = (slots24Hour, slots12Hour, price24Hour, price12Hour) => {
  const parts = [];
  
  if (slots24Hour > 0) {
    parts.push(`${slots24Hour} × 24hr slot (₹${price24Hour.toLocaleString()} each) = ₹${(slots24Hour * price24Hour).toLocaleString()}`);
  }
  
  if (slots12Hour > 0) {
    parts.push(`${slots12Hour} × 12hr slot (₹${price12Hour.toLocaleString()} each) = ₹${(slots12Hour * price12Hour).toLocaleString()}`);
  }
  
  return parts.join(' + ');
};

/**
 * Format duration in human-readable format
 * @param {number} hours - Total hours
 * @returns {string} - Formatted duration
 */
export const formatDuration = (hours) => {
  if (hours < 1) {
    return '0 hours';
  }
  
  const days = Math.floor(hours / 24);
  const remainingHours = Math.floor(hours % 24);
  
  const parts = [];
  if (days > 0) {
    parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  }
  if (remainingHours > 0) {
    parts.push(`${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`);
  }
  
  return parts.join(' ');
};

// ==================== AVAILABILITY CHECKING FUNCTIONS ====================

/**
 * Check if date-time ranges overlap
 * @param {string} start1 - First range start (ISO datetime)
 * @param {string} end1 - First range end (ISO datetime)
 * @param {string} start2 - Second range start (ISO datetime)
 * @param {string} end2 - Second range end (ISO datetime)
 * @returns {boolean} - True if ranges overlap
 */
export const doDateTimeRangesOverlap = (start1, end1, start2, end2) => {
  const s1 = new Date(start1).getTime();
  const e1 = new Date(end1).getTime();
  const s2 = new Date(start2).getTime();
  const e2 = new Date(end2).getTime();
  
  // Check for overlap: start1 < end2 AND end1 > start2
  return s1 < e2 && e1 > s2;
};

/**
 * Check if a car is available for the requested date-time range
 * @param {string} carId - Car ID
 * @param {string} fromDateTime - Start datetime (ISO format)
 * @param {string} toDateTime - End datetime (ISO format)
 * @param {string} excludeBookingId - Booking ID to exclude (for editing)
 * @returns {boolean} - True if available
 */
export const isCarAvailable = (carId, fromDateTime, toDateTime, excludeBookingId = null, bookingsList = [], blocksList = null) => {
  if (!bookingsList || !Array.isArray(bookingsList)) {
    console.warn('isCarAvailable: bookingsList is required and must be an array');
    return true; // If no bookings provided, assume available
  }
  
  const bookings = bookingsList.filter(
    booking => 
      booking.carId === carId && 
      booking.status === 'confirmed' && // Only confirmed bookings block calendar
      booking.id !== excludeBookingId
  );
  
  // Check if requested range overlaps with any existing booking
  for (const booking of bookings) {
    if (doDateTimeRangesOverlap(fromDateTime, toDateTime, booking.fromDateTime, booking.toDateTime)) {
      return false;
    }
  }
  
  // Check if requested range overlaps with any car block
  if (blocksList && Array.isArray(blocksList)) {
    for (const block of blocksList) {
      if (block.carId === carId && 
          doDateTimeRangesOverlap(fromDateTime, toDateTime, block.fromDateTime, block.toDateTime)) {
        return false;
      }
    }
  }
  
  return true;
};

/**
 * Get all bookings for a specific car on a specific date
 * @param {string} carId - Car ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Array} bookingsList - Required array of bookings from Firebase
 * @returns {Array} - Array of bookings
 */
export const getBookingsForDate = (carId, date, bookingsList = []) => {
  if (!bookingsList || !Array.isArray(bookingsList)) {
    console.warn('getBookingsForDate: bookingsList is required and must be an array');
    return [];
  }
  
  const bookings = bookingsList;
  
  // Work entirely in IST timezone
  // Convert target date to IST timestamp for comparison
  const targetDateIST = `${date}T00:00:00+05:30`;
  const nextDayDate = new Date(date);
  nextDayDate.setDate(nextDayDate.getDate() + 1);
  const nextDayIST = `${nextDayDate.toISOString().split('T')[0]}T00:00:00+05:30`;
  
  const targetTime = new Date(targetDateIST).getTime();
  const nextDayTime = new Date(nextDayIST).getTime();
  
  return bookings.filter(booking => {
    if (booking.carId !== carId || booking.status !== 'confirmed') { // Only confirmed bookings block calendar
      return false;
    }
    
    const bookingStartTime = new Date(booking.fromDateTime).getTime();
    const bookingEndTime = new Date(booking.toDateTime).getTime();
    
    // Check if booking overlaps with the target date
    return bookingStartTime < nextDayTime && bookingEndTime > targetTime;
  });
};

/**
 * Get blocked hours for a specific date from bookings and car blocks
 * @param {string} carId - Car ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Array} bookingsList - Required array of bookings from Firebase
 * @param {Array} blocksList - Optional array of car blocks
 * @returns {Array} - Array of blocked hour ranges [{from: hour, to: hour}]
 */
export const getBlockedHoursForDate = (carId, date, bookingsList = [], blocksList = null) => {
  const bookings = getBookingsForDate(carId, date, bookingsList);
  const blockedRanges = [];
  
  // Helper function to get IST date and hour from a datetime string
  const getISTDateAndHour = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    // Use toLocaleString with IST timezone to get correct date and hour
    const dateStr = date.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD
    const timeStr = date.toLocaleTimeString('en-US', { 
      timeZone: 'Asia/Kolkata',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    }); // HH:MM
    const hour = parseInt(timeStr.split(':')[0]);
    return { date: dateStr, hour };
  };
  
  // Process bookings
  bookings.forEach(booking => {
    const startIST = getISTDateAndHour(booking.fromDateTime);
    const endIST = getISTDateAndHour(booking.toDateTime);
    
    const bookingStartDate = startIST.date;
    const bookingEndDate = endIST.date;
    const startHourIST = startIST.hour;
    const endHourIST = endIST.hour;
    
    // Calculate which hours of the target date are blocked
    let startHour, endHour;
    
    if (bookingStartDate < date) {
      // Booking started before target date, so entire morning is blocked
      startHour = 0;
    } else if (bookingStartDate === date) {
      // Booking starts on target date
      startHour = startHourIST;
    } else {
      // Booking starts after target date, skip
      return;
    }
    
    if (bookingEndDate > date) {
      // Booking extends past target date, so entire evening is blocked
      endHour = 24;
    } else if (bookingEndDate === date) {
      // Booking ends on target date
      // For end hour, we need to include the hour it ends at (exclusive range)
      // If booking ends at 10 PM (22:00), hours 0-22 are blocked
      endHour = endHourIST;
    } else {
      // Booking ended before target date, skip
      return;
    }
    
    blockedRanges.push({
      from: startHour,
      to: endHour,
      bookingId: booking.id,
      type: 'booking'
    });
  });
  
  // Process car blocks
  if (blocksList && Array.isArray(blocksList)) {
    blocksList.forEach(block => {
      if (block.carId !== carId) {
        return;
      }
      const startIST = getISTDateAndHour(block.fromDateTime);
      const endIST = getISTDateAndHour(block.toDateTime);
      
      const blockStartDate = startIST.date;
      const blockEndDate = endIST.date;
      const startHourIST = startIST.hour;
      const endHourIST = endIST.hour;
      
      // Calculate which hours of the target date are blocked
      let startHour, endHour;
      
      if (blockStartDate < date) {
        // Block started before target date, so entire morning is blocked
        startHour = 0;
      } else if (blockStartDate === date) {
        // Block starts on target date
        startHour = startHourIST;
      } else {
        // Block starts after target date, skip
        return;
      }
      
      if (blockEndDate > date) {
        // Block extends past target date, so entire evening is blocked
        endHour = 24;
      } else if (blockEndDate === date) {
        // Block ends on target date
        endHour = endHourIST;
      } else {
        // Block ended before target date, skip
        return;
      }
      
      blockedRanges.push({
        from: startHour,
        to: endHour,
        blockId: block.id,
        type: 'block'
      });
    });
  }
  
  return blockedRanges;
};

/**
 * Check if a specific date has any availability
 * @param {string} carId - Car ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Array} bookingsList - Required array of bookings from Firebase
 * @param {Array} blocksList - Optional array of car blocks
 * @returns {boolean} - True if date has any availability
 */
export const hasAvailabilityOnDate = (carId, date, bookingsList = [], blocksList = null) => {
  const blockedRanges = getBlockedHoursForDate(carId, date, bookingsList, blocksList);
  
  // If no bookings, date is available
  if (blockedRanges.length === 0) {
    return true;
  }
  
  // Check if entire day (0-24) is blocked
  // Sort ranges by start time
  const sortedRanges = blockedRanges.sort((a, b) => a.from - b.from);
  
  // Merge overlapping ranges
  const mergedRanges = [];
  let current = sortedRanges[0];
  
  for (let i = 1; i < sortedRanges.length; i++) {
    if (sortedRanges[i].from <= current.to) {
      // Overlapping, merge
      current = {
        from: current.from,
        to: Math.max(current.to, sortedRanges[i].to)
      };
    } else {
      mergedRanges.push(current);
      current = sortedRanges[i];
    }
  }
  mergedRanges.push(current);
  
  // Check if entire day is covered
  if (mergedRanges.length === 1 && mergedRanges[0].from === 0 && mergedRanges[0].to === 24) {
    return false;
  }
  
  return true;
};

/**
 * Get availability status for a date
 * @param {string} carId - Car ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Array} bookingsList - Required array of bookings from Firebase
 * @param {Array} blocksList - Optional array of car blocks
 * @returns {string} - 'available', 'partial', or 'unavailable'
 */
export const getDateAvailabilityStatus = (carId, date, bookingsList = [], blocksList = null) => {
  const blockedRanges = getBlockedHoursForDate(carId, date, bookingsList, blocksList);
  
  if (blockedRanges.length === 0) {
    return 'available';
  }
  
  if (!hasAvailabilityOnDate(carId, date, bookingsList, blocksList)) {
    return 'unavailable';
  }
  
  return 'limited';
};

// ==================== TIME/DATE FORMATTING FUNCTIONS ====================

/**
 * Format date-time for display in IST
 * @param {string} dateTimeStr - ISO datetime string with timezone
 * @returns {string} - Formatted string in IST
 */
export const formatDateTime = (dateTimeStr) => {
  const dt = new Date(dateTimeStr);
  return dt.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata' // Explicit IST timezone
  });
};

/**
 * Format date for display in IST
 * @param {string} dateStr - Date string
 * @returns {string} - Formatted date in IST
 */
export const formatDate = (dateStr) => {
  const dt = new Date(dateStr);
  return dt.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Asia/Kolkata' // Explicit IST timezone
  });
};

/**
 * Combine date and time into a datetime string with IST timezone
 * @param {string} date - Date in YYYY-MM-DD format  
 * @param {string} time - Time in HH or HH:MM format
 * @returns {string} - Datetime string with IST offset (YYYY-MM-DDTHH:MM:SS+05:30)
 */
export const combineDateAndTime = (date, time) => {
  // Ensure time is in HH:MM format
  const timeParts = time.includes(':') ? time.split(':') : [time, '00'];
  const hours = timeParts[0].padStart(2, '0');
  const minutes = timeParts[1] ? timeParts[1].padStart(2, '0') : '00';
  
  // Return datetime string WITH IST timezone offset to prevent any shifting
  // Format: YYYY-MM-DDTHH:MM:SS+05:30 (IST offset)
  return `${date}T${hours}:${minutes}:00+05:30`;
};
